/**
 * @file Components/Sections/Newsletter.jsx
 * @description Premium newsletter CTA section with gradient background,
 * animated elements, social proof, and polished subscribe form.
 */

import React, { useState } from 'react';
import { Mail, Send, CheckCircle, Sparkles, Users } from 'lucide-react';

const Newsletter = () => {
  const [email, setEmail] = useState('');
  const [isLoading, setIsLoading] = useState(false);
  const [isSuccess, setIsSuccess] = useState(false);

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!email || !/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(email)) return;
    setIsLoading(true);
    await new Promise(r => setTimeout(r, 1200));
    setIsSuccess(true);
    setEmail('');
    setIsLoading(false);
    setTimeout(() => setIsSuccess(false), 4000);
  };

  return (
    <section className="relative py-20 overflow-hidden">
      {/* Gradient BG */}
      <div className="absolute inset-0 bg-gradient-to-br from-[#1a2463] via-[#243185] to-[#4f5fd6]" />
      <div className="absolute inset-0 opacity-10"
        style={{ backgroundImage: 'radial-gradient(circle at 2px 2px, white 1px, transparent 0)', backgroundSize: '30px 30px' }} />
      {/* Decorative blobs */}
      <div className="absolute top-0 right-0 w-96 h-96 bg-blue-400/10 rounded-full blur-3xl -translate-y-1/2 translate-x-1/2" />
      <div className="absolute bottom-0 left-0 w-80 h-80 bg-indigo-400/10 rounded-full blur-3xl translate-y-1/2 -translate-x-1/2" />

      <div className="relative max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
        <div className="flex items-center justify-center gap-2 mb-4">
          <Sparkles size={20} className="text-amber-400" />
          <span className="text-sm font-bold text-amber-400 uppercase tracking-wider">Exclusive Access</span>
        </div>

        <h2 className="text-3xl md:text-5xl font-bold text-white mb-4 leading-tight">
          Get Secret Deals Before<br />
          <span className="text-transparent bg-clip-text bg-gradient-to-r from-amber-300 to-orange-400">Anyone Else</span>
        </h2>
        <p className="text-white/60 text-lg max-w-lg mx-auto mb-8">
          Join 12,000+ savvy travellers who get our exclusive flash sales, 
          hidden gems, and insider travel tips every week.
        </p>

        {/* Subscribe Form */}
        <form onSubmit={handleSubmit} className="max-w-lg mx-auto">
          <div className="flex bg-white/10 backdrop-blur-sm rounded-full p-1.5 border border-white/20">
            <div className="flex-1 flex items-center gap-2 px-4">
              <Mail size={18} className="text-white/40 flex-shrink-0" />
              <input
                type="email"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                placeholder="Enter your email address"
                className="w-full bg-transparent text-white placeholder:text-white/40 outline-none text-sm font-medium py-2"
                disabled={isLoading}
                required
              />
            </div>
            <button
              type="submit"
              disabled={isLoading || isSuccess}
              className={`px-6 py-3 rounded-full font-bold text-sm flex items-center gap-2 transition-all ${
                isSuccess
                  ? 'bg-emerald-500 text-white'
                  : 'bg-white text-[#243185] hover:bg-gray-100 hover:shadow-lg'
              } disabled:opacity-70`}
            >
              {isLoading ? (
                <><div className="w-4 h-4 border-2 border-[#243185]/30 border-t-[#243185] rounded-full animate-spin" /> Joining...</>
              ) : isSuccess ? (
                <><CheckCircle size={16} /> Subscribed!</>
              ) : (
                <><Send size={14} /> Subscribe</>
              )}
            </button>
          </div>
        </form>

        {/* Social Proof */}
        <div className="flex items-center justify-center gap-3 mt-6">
          <div className="flex -space-x-2">
            {[1,2,3,4].map(i => (
              <div key={i} className="w-8 h-8 rounded-full bg-gradient-to-br from-blue-400 to-indigo-400 border-2 border-[#243185] flex items-center justify-center">
                <Users size={12} className="text-white" />
              </div>
            ))}
          </div>
          <p className="text-white/50 text-sm">
            <span className="text-white font-semibold">12,847</span> travellers already subscribed
          </p>
        </div>

        <p className="text-white/30 text-xs mt-4">
          No spam, ever. Unsubscribe with one click.
        </p>
      </div>
    </section>
  );
};

export default Newsletter;
